import { getLocalizedValue as t } from "../../src/i18n";
import { utils as i18nNamespace } from "../../src/i18n/namespace";
import moment from "moment";

export const daysOfWeek = [
    {label: t("monday", i18nNamespace), value: 1},
    {label: t("tuesday", i18nNamespace), value: 2},
    {label: t("wednesday", i18nNamespace), value: 3},
    {label: t("thursday", i18nNamespace), value: 4},
    {label: t("friday", i18nNamespace), value: 5},
    {label: t("saturday", i18nNamespace), value: 6},
    {label: t("sunday", i18nNamespace), value: 7}
];

export const hours = [
    {label: "12:00", value: 12},
    {label: "13:00", value: 13},
    {label: "14:00", value: 14},
    {label: "15:00", value: 15},
    {label: "16:00", value: 16},
    {label: "17:00", value: 17},
    {label: "18:00", value: 18},
    {label: "19:00", value: 19},
    {label: "20:00", value: 20},
    {label: "21:00", value: 21},
    {label: "22:00", value: 22},
    {label: "23:00", value: 23},
    {label: "00:00", value: 24},
    {label: "01:00", value: 1},
    {label: "02:00", value: 2},
    {label: "03:00", value: 3},
    {label: "04:00", value: 4},
    {label: "05:00", value: 5},
    {label: "06:00", value: 6},
    {label: "07:00", value: 7},
    {label: "08:00", value: 8},
    {label: "09:00", value: 9},
    {label: "10:00", value: 10},
    {label: "11:00", value: 11}
];


export const shortDaysObj = {
    0: 'Sun',
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
    7: 'Sun'
};

export const mainDaysObj = {
    0: {label: t("sun", i18nNamespace), value: 7},
    1: {label: t("mon", i18nNamespace), value: 1},
    2: {label: t("tue", i18nNamespace), value: 2},
    3: {label: t("wed", i18nNamespace), value: 3},
    4: {label: t("thu", i18nNamespace), value: 4},
    5: {label: t("fri", i18nNamespace), value: 5},
    6: {label: t("sat", i18nNamespace), value: 6},

};

/**
 * Calculates the elapsed time between two date-time values in hours.
*
* @param {string|number|Date} startTime - Start timestamp accepted by `new Date(...)`.
* @param {string|number|Date} endTime - End timestamp accepted by `new Date(...)`.
* @returns {number} Difference in hours. Positive when `endTime` is after `startTime`,
* negative when `endTime` is before `startTime`, and `NaN` for invalid inputs.
*/
export const getDiffInHrs = (startTime, endTime) => {
    const diffInMs = new Date(endTime) - new Date(startTime);
    return diffInMs / (1000 * 60 * 60);
};

// Formats a date as "01:30 PM Apr 8, 2026" (h:mm A MMM D, YYYY)
export const formatToTimeDate = (dateValue) => {
    return moment(dateValue).format('h:mm A MMM D, YYYY');
};

// X-axis tick callback: shows label at index 0 and every 3rd point, with optional forecast support
export const stripTimeFromDateLabel = (fullLabel) => {
    // Strip the time portion from labels like "11:00 AM Feb 10, 2020" → "Feb 10, 2020"
    if (!fullLabel) return fullLabel;
    const match = fullLabel.match(/([A-Z][a-z]{2} \d{1,2}, \d{4})$/);
    return match ? match[1] : fullLabel;
};

export const formatDateTime = (dateValue) => {
    return moment(dateValue).format('MMMM D, YYYY, h:mm A');
};

// Format date as abbreviated month and day, e.g. "Jul, 10"
export const formatToMonthDay = (dateValue) => {
    return moment(dateValue).format('MMM, D');
};

// Format date as time + abbreviated month and day (no year), e.g. "1:30 PM Jun 10"
export const formatToTimeDateShort = (dateValue) => {
    return moment(dateValue).format('h:mm A MMM D');
};

// ASUP schedule hours use 24 for midnight (00:00); Date/API hours use 0-23.
const normalizeHourOfDay = (hourOfDay) => (hourOfDay === 24 ? 0 : hourOfDay);
const denormalizeHourOfDay = (hourOfDay) => (hourOfDay === 0 ? 24 : hourOfDay);

const MINUTES_PER_DAY = 24 * 60;

// daysOfWeek values are 1=Mon ... 7=Sun. Date weekdays are 0=Sun ... 6=Sat.
const toJsDay = (dayOfWeek) => (dayOfWeek === 7 ? 0 : dayOfWeek);

const wrapDayOfWeek = (dayOfWeek) => {
    let day = dayOfWeek;
    while (day < 1) {
        day += 7;
    }
    while (day > 7) {
        day -= 7;
    }
    return day;
};

const shiftScheduleByMinutes = (dayOfWeek, hourOfDay, minuteOfHour, deltaMinutes) => {
    let totalMinutes = normalizeHourOfDay(hourOfDay) * 60 + minuteOfHour + deltaMinutes;
    let dayDelta = 0;
    // Borrow a full day when the offset crosses midnight backwards (e.g. IST Monday 00:00 → Sunday).
    while (totalMinutes < 0) {
        totalMinutes += MINUTES_PER_DAY;
        dayDelta -= 1;
    }
    while (totalMinutes >= MINUTES_PER_DAY) {
        totalMinutes -= MINUTES_PER_DAY;
        dayDelta += 1;
    }

    return {
        dayOfWeek: wrapDayOfWeek(dayOfWeek + dayDelta),
        hourOfDay: Math.floor(totalMinutes / 60),
        minuteOfHour: totalMinutes % 60
    };
};

// Offset at the next occurrence of this weekday/time, so DST is taken from that day rather than "now".
const timezoneOffsetAtNextOccurrence = (dayOfWeek, hourOfDay, minuteOfHour, useUtc) => {
    const hour = normalizeHourOfDay(hourOfDay);
    const date = new Date();
    const currentDay = useUtc ? date.getUTCDay() : date.getDay();
    const dayDelta = (toJsDay(dayOfWeek) - currentDay + 7) % 7;
    if (useUtc) {
        date.setUTCDate(date.getUTCDate() + dayDelta);
        date.setUTCHours(hour, minuteOfHour, 0, 0);
        if (date.getTime() <= Date.now()) {
            date.setUTCDate(date.getUTCDate() + 7);
        }
    } else {
        date.setDate(date.getDate() + dayDelta);
        date.setHours(hour, minuteOfHour, 0, 0);
        if (date.getTime() <= Date.now()) {
            date.setDate(date.getDate() + 7);
        }
    }
    return date.getTimezoneOffset();
};

/**
 * Converts an ASUP schedule selected in the browser timezone to UTC for the API.
 * Preserves half-hour (and similar) offsets via minuteOfHour.
 * Date#getTimezoneOffset is minutes to add to local time to get UTC, evaluated on the next occurrence.
 */
export const convertLocalScheduleToUtc = (dayOfWeek, hourOfDay, minuteOfHour = 0) => {
    return shiftScheduleByMinutes(
        dayOfWeek,
        hourOfDay,
        minuteOfHour,
        timezoneOffsetAtNextOccurrence(dayOfWeek, hourOfDay, minuteOfHour, false)
    );
};

/**
 * Converts a UTC ASUP schedule from the API into the browser timezone for display/editing.
 * Maps midnight (0) to the UI hour value 24.
 */
export const convertUtcScheduleToLocal = (dayOfWeek, hourOfDay, minuteOfHour = 0) => {
    const local = shiftScheduleByMinutes(
        dayOfWeek,
        hourOfDay,
        minuteOfHour,
        -timezoneOffsetAtNextOccurrence(dayOfWeek, hourOfDay, minuteOfHour, true)
    );
    return {
        ...local,
        hourOfDay: denormalizeHourOfDay(local.hourOfDay)
    };
};